import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAccess } from '../access/AccessProvider';
import { useDemo } from '../demo/DemoProvider';
import { publicRoutes, freeAccessRoutes, restrictedRoutes } from '../../utils/routes';
import AccessError from '../../pages/AccessError';
import RedirectToAthena from '../../pages/RedirectToAthena';
import Layout from '../Layout';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireApiAccess?: boolean;
}

function shouldRestrictAccess({
  isAuthenticated,
  location,
  access,
  isDemo,
  restrictedRoutes,
}: {
  isAuthenticated: boolean;
  location: any;
  access: any;
  isDemo: boolean;
  restrictedRoutes: any[];
}) {
  const restricted = restrictedRoutes.find((r) => r.route === location.pathname);

  if (!isAuthenticated || !restricted) return false;

  // In demo mode, allow navigating to market aggregates without permission/subscription checks
  if (isDemo && (restricted.route === '/market-aggregates' || restricted.route === '/map')) {
    return false;
  }

  const lacksRequiredPermission =
    restricted.permission &&
    (!access?.permissions || !access.permissions.has(restricted.permission));

  return lacksRequiredPermission;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isPermissionsLoaded } = useAuth();
  const { access, isLoading: isAccessLoading, error: isAccessErrored } = useAccess();
  const { isDemo } = useDemo();
  const location = useLocation();

  // Allow unauthenticated preview on public routes (e.g., '/', '/overview') without waiting on access/permissions
  const isPublic = publicRoutes.includes(location.pathname);

  if (!isAuthenticated && isPublic) {
    return <>{children}</>;
  }

  if (isLoading || isAccessLoading || !isPermissionsLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E34290]"></div>
      </div>
    );
  }

  if (isAccessErrored) {
    return (
      <Layout>
        <AccessError />
      </Layout>
    );
  }

  if (!isAuthenticated && !publicRoutes.includes(location.pathname)) {
    return <Navigate to="/login" replace />;
  }

  // Free users should always be redirected to Athena (they don't have dashboard_v1 access)
  if (isAuthenticated && access?.role === 'user:free') {
    return (
      <Layout>
        <RedirectToAthena isFreeUser={true} />
      </Layout>
    );
  }

  // Show redirect page for users without dashboard_v1 access (e.g., subscriber:tier users)
  // This applies to ALL routes including /payment-required
  const hasDashboardV1Access = access?.features?.has('access:dashboard_v1') ?? false;
  if (isAuthenticated && access && !hasDashboardV1Access) {
    return (
      <Layout>
        <RedirectToAthena isFreeUser={false} />
      </Layout>
    );
  }

  if (shouldRestrictAccess({ isAuthenticated, location, access, isDemo, restrictedRoutes })) {
    return <Navigate to="/account?tab=subscription" replace />;
  }

  if (isAuthenticated && freeAccessRoutes.includes(location.pathname)) {
    return <>{children}</>;
  }

  return <>{children}</>;
}
